import React, { useEffect, useState } from "react";
import { ReceiptText, Edit, Check, X, AlertCircle } from "lucide-react";
import { useAuthorisation } from "src/context";
import { RoleHelper } from "src/helpers";
import { TextInputBox } from "src/components/ui/inputs";

interface EditPaymentTermsProps {
  paymentTerms?: string;
  onUpdatePaymentTerms?: (paymentTerms: string | null) => void;
  isUpdatingPaymentTerms?: boolean;
  paymentTermsUpdateError?: Error | null;
}

const EditPaymentTerms: React.FC<EditPaymentTermsProps> = ({
  paymentTerms,
  onUpdatePaymentTerms,
  isUpdatingPaymentTerms = false,
  paymentTermsUpdateError
}) => {
  const { userRole } = useAuthorisation();

  const isFinanceUser = RoleHelper.isHealfOperationsTeam(userRole) || userRole === "HEALF_ACCOUNTANT";
  const isOpsUser = RoleHelper.isHealfOperationsTeam(userRole);
  const isAdminRole = userRole === "ADMIN";

  const isEditable = !!onUpdatePaymentTerms && (isFinanceUser || isOpsUser || isAdminRole);

  const [isEditing, setIsEditing] = useState(false);
  const [draftPaymentTerms, setDraftPaymentTerms] = useState<string>(paymentTerms ?? "");

  // Keep the draft in sync with the latest persisted value from the server
  useEffect(() => {
    setDraftPaymentTerms(paymentTerms ?? "");
  }, [paymentTerms]);

  // If an error occurs during update, reopen the editor so the user can adjust and retry
  useEffect(() => {
    if (paymentTermsUpdateError) {
      setIsEditing(true);
    }
  }, [paymentTermsUpdateError]);

  const handleSave = () => {
    if (!onUpdatePaymentTerms) {
      return;
    }

    const trimmed = draftPaymentTerms.trim();
    onUpdatePaymentTerms(trimmed.length > 0 ? trimmed : null);
    // Immediately close the editor; header will show a loading spinner while the update is in-flight.
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftPaymentTerms(paymentTerms ?? "");
    setIsEditing(false);
  };

  return (
    <div className="w-full max-w-xs rounded-xl border border-secondary-200 bg-white/90 backdrop-blur-sm p-4 shadow-soft">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-2">
          <div className="rounded-full bg-primary-50 p-2">
            <ReceiptText className="w-4 h-4 text-primary-600" />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wide text-secondary-600 uppercase">Payment Terms</p>
          </div>
        </div>
        {isUpdatingPaymentTerms ? (
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        ) : (
          isEditable &&
          !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 rounded-lg hover:bg-secondary-100 transition-colors text-secondary-600 hover:text-primary-600"
              title="Edit payment terms"
            >
              <Edit className="w-4 h-4" />
            </button>
          )
        )}
      </div>
      {isEditing ? (
        <div className="space-y-3 mt-3">
          <TextInputBox
            id="payment-terms-input"
            inputValue={draftPaymentTerms}
            onInputChange={(value) => setDraftPaymentTerms(value)}
            maxLength={10}
            disabled={isUpdatingPaymentTerms}
            placeholder="Enter payment terms"
            size="sm"
            className="text-sm"
          />
          {paymentTermsUpdateError && (
            <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-error-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error-800">Failed to update payment terms</p>
                <p className="text-xs text-error-700 mt-1">
                  {paymentTermsUpdateError.message || "An error occurred. Please try again."}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isUpdatingPaymentTerms}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {isUpdatingPaymentTerms ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isUpdatingPaymentTerms}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-secondary-900 min-h-[1.5rem]">
          {paymentTerms && paymentTerms.trim().length > 0 ? paymentTerms : "-"}
        </p>
      )}
    </div>
  );
};

export { EditPaymentTerms };
